import React, { useEffect, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { Box, Button, Typography, useTheme, useMediaQuery } from "@mui/material";
import PageLoader from "components/PageLoader";
import { sourceRows, SOURCE_COLORS } from "components/analytics/data";
import ChartTooltip from 'components/analytics/ChartTooltip';
import InvoiceBar from 'components/analytics/InvoiceBar';

const DEFAULT_COLOR = "#D3D3D3";
const monthNumber = (month) => {
  const numericMonth = Number(month);
  if (Number.isInteger(numericMonth) && numericMonth >= 1 && numericMonth <= 12) return numericMonth;
  const monthNames = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
  const index = monthNames.indexOf(String(month || "").toLowerCase());
  return index < 0 ? 1 : index + 1;
};

const OverviewChart = ({
  data = {},
  startYear,
  endYear,
  view = "all",
  company = "all",
  sourceUser = "all",
  isMonthly = false,
  selectedYear,
  bgColor,
  isDataLoading = false,
  onResetYearRange,
}) => {
  const theme = useTheme();
  const narrow = useMediaQuery("(max-width:900px)");
  const dark = theme.palette.mode === "dark";
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const chartText = dark ? "#cbd5e1" : "#54688b";
  const [selectedSource, setSelectedSource] = useState(null);

  const getChartTitle = () => {
    if (selectedSource) {
      if (view !== "all")
        return `Customer: ${view} - ${selectedSource}`;

      if (sourceUser !== "all")
        return `Source: ${sourceUser}`;

      if (company !== "all")
        return `Company: ${company} - ${selectedSource}`;

      return `Source: ${selectedSource}`;
    }

    if (view !== "all") return `Customer: ${view}`;
    if (sourceUser !== "all") return `Source: ${sourceUser}`;
    if (company !== "all") return `Company: ${company}`;

    return "Invoices";
  };

  const chartData = (() => {
    if (!startYear || !endYear) return [];

    let timeBuckets = [];

    if (isMonthly && selectedYear) {
      timeBuckets = Array.from({ length: 12 }, (_, i) => i + 1).map(
        (m) => ({
          key: `${selectedYear}-${String(m).padStart(2, "0")}`,
          label: new Date(
            selectedYear,
            m - 1
          ).toLocaleString("default", {
            month: "short",
          }),
        })
      );
    } else {
      timeBuckets = Array.from(
        { length: Number(endYear) - Number(startYear) + 1 },
        (_, i) => Number(startYear) + i
      ).map((y) => ({
        key: String(y),
        label: String(y),
      }));
    }

    const systemData = data?.SystemcodeData || [];

    const hasInvoiceData = (sys) => {
      const monthlyTotal = (sys.monthlyData || []).reduce(
        (sum, m) => sum + Number(m.totalInvoices || 0),
        0
      );

      const customerTotal = (sys.customers || []).reduce(
        (sum, cust) =>
          sum + (cust.invoices?.length || 0),
        0
      );

      return monthlyTotal > 0 || customerTotal > 0;
    };

    let sources = [];

    if (company !== "all" && sourceUser === "all") {
      sources = systemData
        .filter(hasInvoiceData)
        .map((sys) => ({
          id: sys.SourceSystemCode,
          label: sys.SourceSystemCode,
          monthlyData: sys.monthlyData || [],
          customers: sys.customers || [],
        }));
    } else if (sourceUser !== "all") {
      const single = systemData.find(
        (s) => s.SourceSystemCode === sourceUser
      );

      sources =
        single && hasInvoiceData(single)
          ? [
              {
                ...single,
                id: sourceUser,
                label: sourceUser,
              },
            ]
          : [];
    } else {
      sources = systemData
        .filter(hasInvoiceData)
        .map((sys) => ({
          id: sys.SourceSystemCode,
          label: sys.SourceSystemCode,
          monthlyData: sys.monthlyData || [],
          customers: sys.customers || [],
        }));
    }

    const aggMap = timeBuckets.reduce((acc, bucket) => {
      acc[bucket.key] = {
        time: bucket.key,
        label: bucket.label,
      };

      sources.forEach((s) => {
        acc[bucket.key][s.id] = 0;
      });

      return acc;
    }, {});

    sources.forEach((source) => {
      const monthly = source.monthlyData || [];

      if (monthly.length > 0) {
        monthly.forEach((m) => {
          const yr = Number(m.year);

          if (
            yr < Number(startYear) ||
            yr > Number(endYear)
          )
            return;

          const key =
            isMonthly &&
            yr === Number(selectedYear)
              ? `${yr}-${String(
                  monthNumber(m.month)
                ).padStart(2, "0")}`
              : String(yr);

          if (aggMap[key]) {
            aggMap[key][source.id] += Number(
              m.totalInvoices || 0
            );
          }
        });
      } else if (source.customers?.length) {
        source.customers.forEach((cust) => {
          cust.invoices?.forEach((inv) => {
            const date = new Date(inv.InvoiceDate);

            if (isNaN(date.getTime())) return;

            const yr = date.getFullYear();
            const mo = date.getMonth() + 1;

            if (
              yr < Number(startYear) ||
              yr > Number(endYear)
            )
              return;

            const key =
              isMonthly &&
              yr === Number(selectedYear)
                ? `${yr}-${String(mo).padStart(
                    2,
                    "0"
                  )}`
                : String(yr);

            if (aggMap[key]) {
              aggMap[key][source.id] += 1;
            }
          });
        });
      }
    });

    let rows = Object.values(aggMap);

    // The API can contain valid overall invoice history even when per-source
    // history has not been backfilled. Use it for the unfiltered dashboard.
    const hasSourceInvoices = rows.some((row) =>
      sources.some((source) => Number(row[source.id] || 0) > 0)
    );
    if (!hasSourceInvoices && company === "all" && sourceUser === "all" && view === "all") {
      const overallMonthly = data?.monthlyData || [];
      overallMonthly.forEach((item) => {
        const year = Number(item.year);
        if (year < Number(startYear) || year > Number(endYear)) return;
        const key = isMonthly && year === Number(selectedYear)
          ? `${year}-${String(monthNumber(item.month)).padStart(2, "0")}`
          : String(year);
        if (aggMap[key]) aggMap[key]["All Invoices"] = (aggMap[key]["All Invoices"] || 0) + Number(item.totalInvoices || 0);
      });
      rows = Object.values(aggMap);
    }

    return rows;
  })();

  useEffect(() => {
    if (selectedSource && !chartData.some((row) => Object.prototype.hasOwnProperty.call(row, selectedSource))) {
      setSelectedSource(null);
    }
  }, [chartData, selectedSource]);

  const {
  visibleKeys,
  colorMap,
  periodTotals,
} = (() => {

  if (chartData.length === 0) {
    return {
      visibleKeys: [],
      colorMap: {},
      periodTotals: [],
    };
  }

  const totals = {};
  const periodTotalsLocal = [];

  chartData.forEach((period) => {

    let periodSum = 0;

    Object.entries(period).forEach(([key, value]) => {

      if (key === "time" || key === "label") return;
      if (selectedSource && key !== selectedSource) return;

      const num = Number(value || 0);

      // IMPORTANT FIX
      if (num > 0) {
        totals[key] = (totals[key] || 0) + num;
        periodSum += num;
      }
    });

    periodTotalsLocal.push(periodSum);
  });

  // ONLY companies actually visible in chart
  const activeKeys = Object.keys(totals).filter(
    (key) => totals[key] > 0
  );

  // SORT descending
  const selectedKeys = selectedSource
    ? activeKeys.filter((key) => key === selectedSource)
    : activeKeys;
  selectedKeys.sort((a, b) => totals[b] - totals[a]);

  const map = {};

  selectedKeys.forEach((id, idx) => {
    map[id] =
      sourceRows(data).find((source) => source.id === id)?.color || SOURCE_COLORS[idx % SOURCE_COLORS.length] || DEFAULT_COLOR;
  });

  return {
    visibleKeys: selectedKeys,
    colorMap: map,
    periodTotals: periodTotalsLocal,
  };

})();

  const hasVisibleData =
    visibleKeys.length > 0 &&
    chartData.some((d) =>
      visibleKeys.some(
        (source) =>
          Number(d[source] || 0) > 0
      )
    );

  const isLoading = isDataLoading || !data || chartData.length === 0;

  const handleBarClick = (bar) => {
    const clicked = bar.id;

    setSelectedSource((prev) =>
      prev === clicked ? null : clicked
    );
  };

  if (isLoading) {
    return <PageLoader variant="region" minHeight={300} label="Loading invoice history…" />;
  }

  if (!hasVisibleData) {
    const totalInvoices = Number(data?.totalInvoices || 0);
    const isDark = theme.palette.mode === "dark";
    const primaryText = isDark ? "#e8eaf6" : "#111827";
    const secondaryText = isDark ? "#b8c2d4" : "#475569";
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap={1.5}
        sx={{
          color: primaryText,
          textAlign: "center",
          p: 3,
        }}
      >
        {totalInvoices > 0 ? (
          <>
            <Typography variant="h6" sx={{ fontSize: 18, color: primaryText, fontWeight: 700 }}>No invoices match this date range</Typography>
            <Typography variant="body2" sx={{ color: secondaryText }}>{totalInvoices.toLocaleString()} invoices are recorded for the current filters. Expand the year range to see their history.</Typography>
            {onResetYearRange && (
              <Button onClick={onResetYearRange} variant="outlined" size="small" sx={{ mt: 0.5, color: primaryText, borderColor: isDark ? "#90caf9" : "#2563eb" }}>
                Show all years
              </Button>
            )}
          </>
        ) : (
          <Typography variant="h6" sx={{ fontSize: 18, color: primaryText, fontWeight: 700 }}>No invoice data is available for this selection</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      height="100%"
      width="100%"
      position="relative"
      sx={{
        backgroundColor:
          bgColor || "transparent",
        '& .analytics-invoice-bar': { transformBox: 'fill-box', transformOrigin: 'center bottom', transition: 'transform 180ms ease, filter 180ms ease' },
        '& .analytics-invoice-bar:hover, & .analytics-invoice-bar:focus-within': { transform: 'scale(1.035, 1.008)', filter: 'drop-shadow(0 3px 4px #101d3a25)' },
        '@media (prefers-reduced-motion: reduce)': { '& .analytics-invoice-bar': { transition: 'none' }, '& .analytics-invoice-bar:hover': { transform: 'none' } },
      }}
    >
      {selectedSource && <Button color="info" size="small" onClick={() => setSelectedSource(null)} sx={{ position: 'absolute', top: 0, left: 60, zIndex: 1 }}>Show all sources · {getChartTitle()}</Button>}
      <Box sx={{ height: 'calc(100% - 48px)', minHeight: 180 }}>
      <ResponsiveBar
        barComponent={InvoiceBar}
        animate={!reducedMotion}
        motionConfig="gentle"
        isFocusable
        barAriaLabel={(bar) => `${bar.id}, ${bar.indexValue}: ${Number(bar.value || 0).toLocaleString()} invoices`}
        data={chartData}
        keys={visibleKeys}
        indexBy="time"
        groupMode="stacked"
        margin={{
          top: 28,
          right: 18,
          bottom: 48,
          left: narrow ? 60 : 72,
        }}
        padding={0.25}
        colors={({ id }) =>
          colorMap[id] || DEFAULT_COLOR
        }
        borderRadius={4}
        axisBottom={{
          tickSize: 5,
          tickPadding: 10,
          legend: isMonthly
            ? "Month"
            : "Year",
          legendOffset: 38,
          legendPosition: "middle",
          format: (d) =>
            chartData.find(
              (r) => r.time === d
            )?.label || d,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          legend: "Total Invoices",
          legendOffset: -55,
          legendPosition: "middle",
          format: (v) =>
            String(v),
        }}
        enableLabel={false}
        label={(d) => {
          const value = d.value;

          if (value <= 0) return null;

          const periodTotal =
            periodTotals[d.index] || 1;

          if (
            value < 15 ||
            value / periodTotal < 0.08
          ) {
            return null;
          }

          return String(value);
        }}
        labelSkipWidth={18}
        labelSkipHeight={22}
        labelTextColor={{
          from: "color",
          modifiers: [["darker", 1.4]],
        }}
        isInteractive={true}
        onClick={handleBarClick}
        tooltip={({
          id,
          value,
          indexValue,
          color,
          data,
        }) => {
          const total =
            periodTotals[chartData.findIndex((row) => String(row.time) === String(data?.time ?? indexValue))] || 0;

          const period = chartData.find((row) => String(row.time) === String(indexValue))?.label || indexValue;
          return <ChartTooltip label={`${id} · ${period}`} value={`${Number(value || 0).toLocaleString()} invoices`} color={color}
            detail={total > 0 ? `Period total: ${total.toLocaleString()} · ${(Number(value || 0) / total * 100).toFixed(1)}%` : undefined} />;
        }}
        layers={[
          "grid",
          "axes",
          "bars",
          ({ bars }) => {
            const totals = new Map();
            bars.forEach((bar) => {
              const key = bar.data.indexValue;
              const current = totals.get(key) || { value: 0, x: bar.x + bar.width / 2, y: bar.y, width: bar.width };
              current.value += Number(bar.data.value || 0);
              current.y = Math.min(current.y, bar.y);
              totals.set(key, current);
            });
            return <g aria-hidden="true">{[...totals.entries()].filter(([, total]) => total.width >= 26 && total.value > 0).map(([key, total]) => <text key={key} x={total.x} y={total.y - 7} textAnchor="middle" fill={chartText} fontSize={10} fontWeight={700}>{total.value.toLocaleString()}</text>)}</g>;
          },
        ]}
        theme={{
          axis: {
            ticks: {
              text: { fill: chartText },
            },
            legend: {
              text: {
                fill: chartText,
                fontWeight: 600,
              },
            },
          },
          grid: {
            line: {
              stroke: dark ? "#ffffff12" : "#e8f0fe",
            },
          },
          labels: {
            text: {
              fontSize: 11,
              fontWeight: 600,
              fill: chartText,
            },
          },
        }}
      />

      </Box>
      {/* Source legend also supports keyboard filtering. */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          maxHeight: 48, overflowY: "auto", justifyContent: "center",
        }}
      >
        {visibleKeys.map((key) => (
          <Box
            component="button"
            type="button"
            onClick={() => setSelectedSource((previous) => previous === key ? null : key)}
            aria-pressed={selectedSource === key}
            sx={{ background: "transparent", border: 0, cursor: "pointer", p: 0.5, color: chartText, borderRadius: 1, "&:focus-visible": { outline: "2px solid #1689ff" } }}
            key={key}
            display="flex"
            alignItems="center"
            gap={1}
          >
            <Box
              width={16}
              height={16}
              borderRadius="3px"
              sx={{
                backgroundColor:
                  colorMap[key] ||
                  DEFAULT_COLOR,
              }}
            />

            <Typography
              variant="body2"
              sx={{
                fontSize: 11,
                fontWeight: 500,
                color: chartText,
              }}
            >
              {key}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default React.memo(OverviewChart);
